import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";

const BASE = "/v1/audio";

/** Categories supported by the API today. */
export const AUDIO_CATEGORIES = ["BGM", "EFFECT"];

export const AUDIO_SOURCE_LICENSED = "licensed";

/**
 * Known origin sites for the optional {@code source} field.
 * Values are stored as the label string on {@code audio.source}.
 * @see https://incompetech.com
 * @see https://mixkit.co
 * @see https://pixabay.com
 */
export const AUDIO_SOURCES = [
    {
        id: "licensed",
        label: AUDIO_SOURCE_LICENSED,
        hint: "Purchased files licensed for commercial use",
    },
    {
        id: "Incompetech",
        label: "Incompetech",
        url: "https://incompetech.com",
        hint: "Royalty-free music by Kevin MacLeod",
    },
    {
        id: "Mixkit",
        label: "Mixkit",
        url: "https://mixkit.co",
        hint: "Free stock music and sound effects",
    },
    {
        id: "Pixabay",
        label: "Pixabay",
        url: "https://pixabay.com",
        hint: "Royalty-free music and sound effects",
    },
];

export const AUDIO_SOURCE_LABELS = AUDIO_SOURCES.map((s) => s.label);

function parseEnvelope(json) {
    if (json?.code === "FAILURE") {
        return { success: false, error: json.message || "Request failed" };
    }
    if (json?.code === "NO_DATA") {
        return { success: false, code: "NO_DATA", error: json.message || "Not found" };
    }
    if (json?.code === "SUCCESS") {
        return { success: true, data: json };
    }
    return { success: true, data: json };
}

const AudioService = {
    list: async (page = 0, pageSize = 20, sort = "createdAt:desc", category = null, keyword = null, source = null) => {
        try {
            const params = new URLSearchParams();
            params.append("page", String(page));
            params.append("pageSize", String(pageSize));
            params.append("sort", sort);
            if (category) params.append("category", category);
            if (keyword && String(keyword).trim()) params.append("keyword", String(keyword).trim());
            if (source && String(source).trim()) params.append("source", String(source).trim());
            const response = await ApiService.getData(`${BASE}?${params.toString()}`);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            const content = response?.content ?? [];
            return {
                success: true,
                data: {
                    content,
                    totalElements: response?.totalElements ?? content.length,
                    totalPages: response?.totalPages ?? 0,
                },
            };
        } catch (e) {
            Logger.error("AudioService.list:", e);
            return { success: false, error: e.message || "Failed to list audio" };
        }
    },

    getById: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.getData(`${BASE}/${encodeURIComponent(id)}`);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            if (response?.id) return { success: true, data: response };
            return { success: false, error: "Unexpected response" };
        } catch (e) {
            Logger.error("AudioService.getById:", e);
            return { success: false, error: e.message || "Failed to load audio" };
        }
    },

    /** POST multipart — MP3, WAV, and CAF are stored in their uploaded format. */
    create: async ({ file, name, category, tags, enable = false, source = null }) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("name", name.trim());
            formData.append("category", category);
            if (tags != null && String(tags).trim() !== "") {
                formData.append("tags", String(tags).trim());
            }
            formData.append("enable", enable ? "true" : "false");
            formData.append("source", source == null ? "" : String(source).trim());
            const response = await ApiService.postFormData(BASE, formData);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            if (response?.id) return { success: true, data: response };
            return { success: false, error: "Unexpected response" };
        } catch (e) {
            Logger.error("AudioService.create:", e);
            return { success: false, error: e.message || "Failed to create audio" };
        }
    },

    /** PUT multipart — optional MP3, WAV, or CAF file replacement. */
    update: async (id, { file, name, category, tags, tagsTouched, enable, includeEnable = false, source, sourceTouched = false }) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const formData = new FormData();
            if (file) formData.append("file", file);
            if (name != null) formData.append("name", String(name).trim());
            if (category != null) formData.append("category", category);
            if (tagsTouched) formData.append("tags", tags == null ? "" : String(tags).trim());
            if (includeEnable) formData.append("enable", enable ? "true" : "false");
            if (sourceTouched) formData.append("source", source == null ? "" : String(source).trim());
            const response = await ApiService.putFormData(
                `${BASE}/${encodeURIComponent(id)}`,
                formData
            );
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            if (response?.id) return { success: true, data: response };
            return { success: false, error: "Unexpected response" };
        } catch (e) {
            Logger.error("AudioService.update:", e);
            return { success: false, error: e.message || "Failed to update audio" };
        }
    },

    /**
     * GET /v1/audio/distinct-tags — `{ tag, count }[]` (case-folded like topics).
     */
    listDistinctTags: async () => {
        try {
            const response = await ApiService.getData(`${BASE}/distinct-tags`);
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Request failed", data: [] };
            }
            const raw = Array.isArray(response) ? response : [];
            const data = raw
                .map((item) => ({
                    tag: String(item?.tag ?? "").trim(),
                    count: Number(item?.count) || 0,
                }))
                .filter((item) => item.tag);
            data.sort((a, b) => a.tag.localeCompare(b.tag, undefined, { sensitivity: "base" }));
            return { success: true, data };
        } catch (e) {
            Logger.error("AudioService.listDistinctTags:", e);
            return { success: false, error: e.message || "Failed to load distinct tags", data: [] };
        }
    },

    /** Tag strings for autocomplete (from distinct-tags). */
    listTagSuggestions: async () => {
        const result = await AudioService.listDistinctTags();
        if (!result.success) return result;
        return {
            success: true,
            data: result.data.map((row) => row.tag),
        };
    },

    delete: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.deleteData(`${BASE}/${encodeURIComponent(id)}`);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            return { success: true };
        } catch (e) {
            Logger.error("AudioService.delete:", e);
            return { success: false, error: e.message || "Failed to delete audio" };
        }
    },

    /** GET /v1/audio/local-library — manifest-backed folders on the backend host. */
    listLocalLibraries: async () => {
        try {
            const response = await ApiService.getData(`${BASE}/local-library`);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            if (response?.libraries) {
                return { success: true, data: response };
            }
            return { success: true, data: parsed.data ?? response };
        } catch (e) {
            Logger.error("AudioService.listLocalLibraries:", e);
            return { success: false, error: e.message || "Failed to load local libraries" };
        }
    },

    /** Authenticated blob for local preview (MP4/WAV/etc.). Caller must revoke object URL. */
    previewLocalLibraryBlob: async (libraryId, relativePath) => {
        const params = new URLSearchParams();
        params.set("libraryId", libraryId);
        params.set("relativePath", relativePath);
        return ApiService.downloadFile(`${BASE}/local-library/preview?${params.toString()}`);
    },

    /** POST /v1/audio/local-library/upload — read file from disk on server and store in S3. */
    uploadFromLocalLibrary: async ({
        libraryId,
        relativePath,
        name,
        category = null,
        tags = null,
        enable = false,
        source = null,
    }) => {
        try {
            const formData = new FormData();
            formData.append("libraryId", libraryId);
            formData.append("relativePath", relativePath);
            formData.append("name", String(name).trim());
            if (category) formData.append("category", String(category).trim().toUpperCase());
            if (tags != null && String(tags).trim() !== "") {
                formData.append("tags", String(tags).trim());
            }
            formData.append("enable", enable ? "true" : "false");
            formData.append("source", AUDIO_SOURCE_LICENSED);
            const response = await ApiService.postFormData(`${BASE}/local-library/upload`, formData);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            if (response?.id) return { success: true, data: response };
            return { success: false, error: "Unexpected response" };
        } catch (e) {
            Logger.error("AudioService.uploadFromLocalLibrary:", e);
            return { success: false, error: e.message || "Failed to upload from local library" };
        }
    },

    /** POST /v1/audio/local-library/unsuitable — persist local-file review flag. */
    setLocalUnsuitable: async ({ libraryId, relativePath, unsuitable }) => {
        try {
            const formData = new FormData();
            formData.append("libraryId", libraryId);
            formData.append("relativePath", relativePath);
            formData.append("unsuitable", unsuitable ? "true" : "false");
            const response = await ApiService.postFormData(`${BASE}/local-library/unsuitable`, formData);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AudioService.setLocalUnsuitable:", e);
            return { success: false, error: e.message || "Failed to update unsuitable flag" };
        }
    },

    /** POST /v1/audio/local-library/ready-to-review — persist local-file review flag. */
    setLocalReadyToReview: async ({ libraryId, relativePath, readyToReview }) => {
        try {
            const formData = new FormData();
            formData.append("libraryId", libraryId);
            formData.append("relativePath", relativePath);
            formData.append("readyToReview", readyToReview ? "true" : "false");
            const response = await ApiService.postFormData(`${BASE}/local-library/ready-to-review`, formData);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AudioService.setLocalReadyToReview:", e);
            return { success: false, error: e.message || "Failed to update ready-to-review flag" };
        }
    },
};

export default AudioService;
